import { memo, useCallback, useRef, useState } from "react";
import { useParams } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import { externalLink } from "@/routes/_constants";

import HelpIcon from "@/components/ui/icon/help";

import { usePanelResize } from "./hooks/use-panel-resize";

const PANEL_BOTTOM_OFFSET = 24;
const INITIAL_PANEL_POSITION = { x: 24, y: PANEL_BOTTOM_OFFSET };
const INITIAL_PANEL_WIDTH = 800;
const MIN_PANEL_SIZE = { width: 400, height: 400 };
const MAX_PANEL_WIDTH = 1200;

export const FaqWidget = memo(() => {
	const { hotelID } = useParams<{ hotelID: string }>();
	const [isOpen, setIsOpen] = useState(false);
	// 一度でも開いたら iframe を DOM に残し、閉じた時は非表示にして状態を保持する
	const [hasOpened, setHasOpened] = useState(false);
	const [panelPosition, setPanelPosition] = useState(INITIAL_PANEL_POSITION);
	const [panelSize, setPanelSize] = useState({ width: INITIAL_PANEL_WIDTH, height: 700 });
	// パネルのリサイズを管理するためのref
	const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);
	// パネルの最大サイズを設定
	const maxSize = { width: MAX_PANEL_WIDTH, height: window.innerHeight - PANEL_BOTTOM_OFFSET * 2 };

	const { createResizeHandlers } = usePanelResize({
		panelSize,
		panelPosition,
		setPanelSize,
		setPanelPosition,
		minSize: MIN_PANEL_SIZE,
		maxSize,
	});

	const handleToggle = useCallback(() => {
		setIsOpen((prev) => {
			if (!prev) setHasOpened(true);
			return !prev;
		});
	}, []);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			dragRef.current = {
				startX: e.clientX,
				startY: e.clientY,
				posX: panelPosition.x,
				posY: panelPosition.y,
			};
			e.currentTarget.setPointerCapture(e.pointerId);
		},
		[panelPosition],
	);

	const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
		if (!dragRef.current) return;
		const dx = e.clientX - dragRef.current.startX;
		const dy = e.clientY - dragRef.current.startY;
		setPanelPosition({
			x: dragRef.current.posX - dx,
			y: dragRef.current.posY - dy,
		});
	}, []);

	const handlePointerUp = useCallback(() => {
		dragRef.current = null;
	}, []);

	return (
		<div className="hidden tablet:block">
			{hasOpened && (
				<div
					className={cn(
						"fixed z-[60] flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl",
						isOpen
							? "mb-5 animate-in fade-in-10 zoom-in-95 slide-in-from-bottom-3 slide-in-from-right-4"
							: "hidden",
					)}
					style={{
						right: panelPosition.x,
						bottom: panelPosition.y,
						// パネルの右端が画面の右端にならないように、パネルの幅を設定
						width: Math.min(panelSize.width, window.innerWidth * 0.9),
						// パネルの下部の余白を考慮して、パネルの高さを設定
						height: Math.min(panelSize.height, window.innerHeight - PANEL_BOTTOM_OFFSET * 2),
					}}
				>
					{/* リサイズハンドル - 角 */}
					<div
						className="absolute -left-1 -top-1 z-50 h-3 w-3 cursor-nwse-resize"
						{...createResizeHandlers("nw")}
					/>
					<div
						className="absolute -right-1 -top-1 z-50 h-3 w-3 cursor-nesw-resize"
						{...createResizeHandlers("ne")}
					/>
					<div
						className="absolute -bottom-1 -left-1 z-50 h-3 w-3 cursor-nesw-resize"
						{...createResizeHandlers("sw")}
					/>
					<div
						className="absolute -bottom-1 -right-1 z-50 h-3 w-3 cursor-nwse-resize"
						{...createResizeHandlers("se")}
					/>
					{/* リサイズハンドル - 辺 */}
					<div
						className="absolute -top-1 left-3 right-3 z-50 h-2 cursor-ns-resize"
						{...createResizeHandlers("n")}
					/>
					<div
						className="absolute -bottom-1 left-3 right-3 z-50 h-2 cursor-ns-resize"
						{...createResizeHandlers("s")}
					/>
					<div
						className="absolute -left-1 bottom-3 top-3 z-50 w-2 cursor-ew-resize"
						{...createResizeHandlers("w")}
					/>
					<div
						className="absolute -right-1 bottom-3 top-3 z-50 w-2 cursor-ew-resize"
						{...createResizeHandlers("e")}
					/>
					<div
						className="flex cursor-grab items-center justify-between border-b border-zinc-200 px-4 py-3 active:cursor-grabbing"
						onPointerDown={handlePointerDown}
						onPointerMove={handlePointerMove}
						onPointerUp={handlePointerUp}
					>
						<h3 className="select-none text-sm font-semibold">困ったときは</h3>
						<button
							type="button"
							onClick={handleToggle}
							onPointerDown={(e) => e.stopPropagation()}
							className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<line x1="5" y1="12" x2="19" y2="12" />
							</svg>
						</button>
					</div>
					<iframe
						src={externalLink.faqSiteUrl(hotelID ?? "")}
						className="flex-1 border-0"
						title="FAQ"
					/>
				</div>
			)}

			<button
				type="button"
				onClick={handleToggle}
				className="group fixed bottom-6 right-6 z-[60] inline-flex items-center rounded-full bg-zinc-800 p-4 text-sm font-medium text-white shadow-lg transition-all hover:bg-zinc-700 hover:pr-6 print:hidden"
			>
				<HelpIcon size="lg" className="text-white" />
				<span className="max-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:ml-1.5 group-hover:max-w-[6rem]">
					困ったときは
				</span>
			</button>
		</div>
	);
});

FaqWidget.displayName = "FaqWidget";
